import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { adsMemberApi } from '../../api/adsMemberApi';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui';
import './ads.css';

export default function AdsDashboardPage() {
  const { t } = useTravelTranslation();
  const [tab, setTab] = useState('promotions'); // 'promotions' | 'creatives' | 'campaigns'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorModel, setErrorModel] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    setErrorModel(null);

    let request;
    if (tab === 'promotions') {
      request = adsMemberApi.promotions.listMine();
    } else if (tab === 'creatives') {
      request = adsMemberApi.creatives.listMine();
    } else {
      request = adsMemberApi.campaigns.listMine();
    }

    request
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        setErrorModel(mapApiErrorToUiModel(err, { t }));
        setLoading(false);
      });
  }, [tab, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const errorCopy = errorModel ? resolveUiErrorCopy(errorModel, t) : null;

  return (
    <main className="ads-page" data-testid="ads-dashboard-page">
      <header className="ads-header">
        <h1 className="ads-title">{t('travel.ads.title')}</h1>
        <div className="ads-header__actions">
          <Link
            to="/me/ads/promotions/new"
            className="ads-btn ads-btn--primary"
            data-testid="ads-btn-create-promotion"
          >
            {t('travel.ads.promotions.create')}
          </Link>
          <Link
            to="/me/ads/campaigns/new"
            className="ads-btn ads-btn--secondary"
            data-testid="ads-btn-create-campaign"
          >
            {t('travel.ads.campaigns.create')}
          </Link>
        </div>
      </header>

      <nav className="ads-tabs" aria-label={t('travel.ads.title')}>
        <button
          type="button"
          className={`ads-tab ${tab === 'promotions' ? 'ads-tab--active' : ''}`}
          onClick={() => setTab('promotions')}
          data-testid="ads-tab-promotions"
        >
          {t('travel.ads.tabs.promotions')}
        </button>
        <button
          type="button"
          className={`ads-tab ${tab === 'creatives' ? 'ads-tab--active' : ''}`}
          onClick={() => setTab('creatives')}
          data-testid="ads-tab-creatives"
        >
          {t('travel.ads.tabs.creatives')}
        </button>
        <button
          type="button"
          className={`ads-tab ${tab === 'campaigns' ? 'ads-tab--active' : ''}`}
          onClick={() => setTab('campaigns')}
          data-testid="ads-tab-campaigns"
        >
          {t('travel.ads.tabs.campaigns')}
        </button>
      </nav>

      {loading ? (
        <LoadingState
          className="ads-loading"
          data-testid="ads-loading"
          label={t('travel.ads.common.loading')}
        />
      ) : null}

      {errorCopy ? (
        <ErrorState
          className="ads-alert ads-alert--error"
          data-testid="ads-error"
          title={errorCopy.title}
          description={errorCopy.description}
          retryable={errorCopy.retryable}
          retryLabel={t('travel.ads.common.refresh')}
          onRetry={loadData}
        />
      ) : null}

      {!loading && !errorModel && items.length === 0 ? (
        <EmptyState
          className="ads-empty"
          data-testid="ads-empty"
          title={
            tab === 'promotions'
              ? t('travel.ads.promotions.empty')
              : tab === 'creatives'
                ? t('travel.ads.creatives.empty')
                : t('travel.ads.campaigns.empty')
          }
        />
      ) : null}

      {!loading && !errorModel && items.length > 0 ? (
        <div className="ads-list" data-testid="ads-list">
          {tab === 'promotions' &&
            items.map((p) => (
              <Link
                key={p.promotionApplicationId}
                to={`/me/ads/promotions/${p.promotionApplicationId}`}
                className="ads-card"
                data-testid={`promotion-card-${p.promotionApplicationId}`}
              >
                <div className="ads-card__header">
                  <h3 className="ads-card__title">
                    推廣 #{p.promotionApplicationId}
                  </h3>
                  <span className={`ads-badge ads-badge--${p.status}`}>
                    {t(`travel.ads.status.${p.status}`) || p.status}
                  </span>
                </div>
                <div className="ads-card__meta">
                  <span>來源內容 ID：{p.sourceContentId}</span>
                  {p.createTime ? <span>建立時間：{p.createTime}</span> : null}
                </div>
                {p.status === 'REJECTED' && p.rejectionReason ? (
                  <div className="ads-card__rejection">
                    <strong>{t('travel.ads.promotions.rejectionReason')}：</strong>
                    {p.rejectionReason}
                  </div>
                ) : null}
              </Link>
            ))}

          {tab === 'creatives' &&
            items.map((c) => (
              <Link
                key={c.creativeId}
                to={`/me/ads/creatives/${c.creativeId}`}
                className="ads-card"
                data-testid={`creative-card-${c.creativeId}`}
              >
                <div className="ads-card__header">
                  <h3 className="ads-card__title">
                    {c.headline || `素材 #${c.creativeId}`}
                  </h3>
                  <span className={`ads-badge ads-badge--${c.status}`}>
                    {t(`travel.ads.status.${c.status}`) || c.status}
                  </span>
                </div>
                {c.summary ? <p>{c.summary}</p> : null}
                <div className="ads-card__meta">
                  <span>素材 ID：{c.creativeId}</span>
                  <span>推廣 ID：{c.promotionApplicationId}</span>
                </div>
                {c.status === 'REJECTED' && c.rejectionReason ? (
                  <div className="ads-card__rejection">
                    <strong>{t('travel.ads.creatives.rejectionReason')}：</strong>
                    {c.rejectionReason}
                  </div>
                ) : null}
              </Link>
            ))}

          {tab === 'campaigns' &&
            items.map((camp) => (
              <Link
                key={camp.campaignId}
                to={`/me/ads/campaigns/${camp.campaignId}`}
                className="ads-card"
                data-testid={`campaign-card-${camp.campaignId}`}
              >
                <div className="ads-card__header">
                  <h3 className="ads-card__title">
                    活動 #{camp.campaignId}
                  </h3>
                  <span className={`ads-badge ads-badge--${camp.status}`}>
                    {t(`travel.ads.status.${camp.status}`) || camp.status}
                  </span>
                </div>
                <div className="ads-card__meta">
                  <span>預算：{camp.budgetAmount} {camp.budgetCurrency}</span>
                  <span>排期：{camp.startAt} ~ {camp.endAt}</span>
                </div>
              </Link>
            ))}
        </div>
      ) : null}
    </main>
  );
}
